/*
 *  -----------------------------------------------------------------------
 *    <copyright file="assign-indications-list.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useBoolean, useTranslation } from 'hooks';
import { OrganisationIndicationFields, StudyFields } from 'queries/generated/fragments.generated';
import { ASSIGN_INDICATIONS, GET_STUDY } from 'queries/studies';
import NotificationService from 'utils/notification';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogTitle, DialogContent, DialogActions } from 'views/components/core/dialog';
import Filter from 'views/components/core/filter';
import ItemList from 'views/components/core/item-list';

import 'views/components/admin/studies/admin-study.scss';

const AssignIndicationsListDialog = ({
    study,
    open,
    onClose,
    availableIndications,
}: {
    study: StudyFields;
    open: boolean;
    onClose: Function;
    availableIndications: OrganisationIndicationFields[];
}) => {
    const { t } = useTranslation();
    const [selectedItems, setSelectedItems] = useState([]);
    const [filter, setFilter] = useState('');
    const [filteredIndications, setFilteredIndications] = useState([]);
    const [assignIndications] = useMutation(ASSIGN_INDICATIONS);
    const saving = useBoolean(false);

    const close = (action?: any) => {
        setSelectedItems([]);
        onClose(action);
    };

    const getAvailableIndications = () =>
        availableIndications.filter((fun: any) =>
            !filter.length ? true : fun.name.toUpperCase().indexOf(filter) >= 0,
        );

    useEffect(() => {
        if (open) setFilteredIndications(getAvailableIndications());
    }, [open, availableIndications, filter]);

    const errorRequest = () => {
        setSelectedItems([]);
        NotificationService.showNotification(t('errorMessage'));
        saving.setFalse();
    };

    const successRequest = (closeModal: boolean) => {
        saving.setFalse();
        NotificationService.showNotification(
            t('admin:indicationSucessfullyAssigned', { amount: selectedItems.length, id: study.recordId }),
        );
        setSelectedItems([]);
        if (closeModal) onClose();
    };

    const associate = (closeModal?: boolean) => {
        const studyIndicationIds = study.indicationsIds?.concat(selectedItems.map((selected) => selected.id));

        saving.setTrue();

        assignIndications({
            variables: { studyId: study.id, reason: '', studyIndicationIds },
            refetchQueries: [{ query: GET_STUDY, variables: { id: study.id } }],
        })
            .then(() => {
                successRequest(closeModal);
            })
            .catch(() => {
                errorRequest();
            });
    };

    return (
        <CustomDialog onClose={close} fullWidth scroll="paper" maxWidth="md" open={open}>
            <DialogTitle id="assign-indication-modal-title">
                {t('admin:assignIndications')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent className="assign-indication-dialog-content assign-dialog-content pb-0">
                <>
                    <Filter id="assign-indication-dialog-filter" onChange={({ text }: any) => setFilter(text)} />
                    <div className="list-info">
                        <p className="text-length">
                            {t('admin:indicationRecordsNumber', { number: filteredIndications?.length })}
                        </p>
                    </div>
                    <ItemList
                        id="assign-indication-list"
                        items={filteredIndications}
                        textField="name"
                        filter={filter}
                        multiselect
                        selectedItems={selectedItems}
                        onSelectionChange={(value: any) => {
                            saving.setFalse();
                            setSelectedItems(value);
                        }}
                    />
                </>
            </DialogContent>

            <DialogActions className="actions-assign-indication flex-wrap pt-0">
                <div className="selected-items-container mb-20">
                    <p>
                        {selectedItems.length}/{filteredIndications?.length}
                    </p>
                </div>
                <Button
                    id="assign-indication-new"
                    text="admin:addOrganizationIndications"
                    secondary
                    disabled={saving.value}
                    onClick={() => {
                        close('new-indication');
                    }}
                />
                <Button
                    text="assign"
                    disabled={!selectedItems.length || saving.value}
                    id="assign-indication-save"
                    onClick={() => {
                        associate(false);
                    }}
                />
                <Button
                    text="ra:assignAndClose"
                    disabled={!selectedItems.length || saving.value}
                    id="assign-indication-save-close"
                    onClick={() => {
                        associate(true);
                    }}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AssignIndicationsListDialog;
